import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Layout } from "@/components/layout/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/hooks/useAuth";

export function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/password-reset/confirm`, {
        token,
        new_password: password,
      });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <section className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Account recovery</p>
        <h1 className="font-display text-4xl mt-1">Choose a new password</h1>

        <Card className="mt-8 p-6">
          {!token ? (
            <p className="text-sm text-destructive">
              This reset link is missing its token. Please request a new one from
              the <Link to="/forgot-password" className="text-primary hover:underline">
              forgot-password page</Link>.
            </p>
          ) : done ? (
            <div data-testid="reset-password-success" className="space-y-3">
              <p className="text-sm">
                Password updated. Redirecting you to the sign-in page…
              </p>
              <Link to="/login" className="text-primary hover:underline text-sm">
                Sign in now →
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="password">New password</Label>
                <Input
                  id="password"
                  data-testid="reset-password-new"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  minLength={8}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm">Confirm new password</Label>
                <Input
                  id="confirm"
                  data-testid="reset-password-confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                  minLength={8}
                />
              </div>
              {error && <p data-testid="reset-password-error" className="text-sm text-destructive">{error}</p>}
              <Button
                data-testid="reset-password-submit"
                type="submit"
                disabled={busy}
                className="w-full rounded-full"
              >
                {busy ? "Updating…" : "Update password"}
              </Button>
            </form>
          )}
        </Card>
      </section>
    </Layout>
  );
}
